/**
 * HNS Player Item Component
 * 
 * HNS-specific component for displaying a player in a round.
 * Shows role badges, DQ status, uses of limited rules, scores, and role toggle,
 * disqualify and revoke DQ buttons.
 */

import { memo } from 'react';
import {
  HideSeekRoundPlayer,
  Round,
  Player,
  CheatType,
  isHnsPlayerDisqualified,
  calculateHnsPlayerRoundScore,
  getPlayerDisplayName,
  getHideSeekRoleAtTime,
  countRuleUses,
  HNS_RULE_KEYS,
  type RulesetChangePayload,
} from '@swtorbit/shared-socket-events';
import { formatDuration } from '@swtorbit/shared-react-hooks';
import { roleBadgeClass } from '@swtorbit/shared-components';

// Get a short label for the cheat type
function getCheatLabel(cheatType: CheatType): string {
  switch (cheatType) {
    case CheatType.STEALTH:
      return 'STEALTH';
    case CheatType.TELEPORT:
      return 'TELEPORT';
    case CheatType.QUICK_TRAVEL:
      return 'TRAVEL';
    case CheatType.TAXI:
      return 'TAXI';
    case CheatType.INTERCEDE:
      return 'INTERCEDE';
    case CheatType.PULL:
      return 'PULL';
    case CheatType.KNOCKBACK:
      return 'KNOCKBACK';
    case CheatType.LEAP:
      return 'LEAP';
    case CheatType.DEATH:
      return 'DEATH';
    case CheatType.CAMERA_DISTANCE:
      return 'CAMERA';
    case CheatType.BANNED_MOUNT:
      return 'MOUNT';
    case CheatType.LEFT_AREA:
      return 'LEFT AREA';
    case CheatType.MANUAL:
      return 'ADMIN DQ';
    default:
      return 'DQ';
  }
}

interface HnsPlayerItemProps {
  player: HideSeekRoundPlayer;
  /**
   * Hide and Seek Teams: a caught hider is out of the round, shown with this
   * label while the round is played as well as after.
   */
  outLabel?: string;
  round: Round;
  roundIndex: number;
  roundStatus: Round['status'];
  gamePlayers: Player[];
  onToggleRole: (roundIndex: number, playerId: string, currentRole: 'hider' | 'seeker') => void;
  /** Disqualify the player from the round in play. */
  onDisqualify: (roundIndex: number, playerId: string) => void;
  /** Lift the player's disqualification. */
  onRevokeDisqualification: (roundIndex: number, playerId: string) => void;
  /** The round's rules in effect now: the player's uses are shown against their role's. */
  rules: RulesetChangePayload;
}

/**
 * The player's uses under their current role's rules: "Quick Travel 1/1" for
 * a limited rule, "Deaths ×2" for an unlimited one they used.
 */
function ruleUseBadges(player: HideSeekRoundPlayer, rules: RulesetChangePayload): Array<{ text: string; usedUp: boolean }> {
  if (!player.ruleUses?.length) return [];
  const role = getHideSeekRoleAtTime(player, Date.now());
  const roleRules = role === 'seeker' ? rules.seekerRules : rules.hiderRules;
  return HNS_RULE_KEYS.flatMap(({ key, label }) => {
    const used = countRuleUses(player, key, role);
    const allowance = roleRules[key];
    if (typeof allowance === 'number') return [{ text: `${label} ${used}/${allowance}`, usedUp: used >= allowance }];
    return used > 0 ? [{ text: `${label} ×${used}`, usedUp: false }] : [];
  });
}

export const HnsPlayerItem = memo(function HnsPlayerItem({
  player,
  round,
  roundIndex,
  roundStatus,
  gamePlayers,
  onToggleRole,
  onDisqualify,
  onRevokeDisqualification,
  outLabel,
  rules,
}: HnsPlayerItemProps) {
  const canToggle = roundStatus === 'setup' || roundStatus === 'hiding' || roundStatus === 'seeking';
  const showScore = roundStatus === 'completed';
  // The server saves each player's score when the round ends; rounds that
  // ended before it did are scored here with the shared scorer.
  const score = showScore ? player.finalScoreSeconds ?? calculateHnsPlayerRoundScore(player, round) : 0;
  const roundIsActive = roundStatus === 'hiding' || roundStatus === 'seeking';

  const penaltySeconds = player.totalPenaltySeconds || 0;
  const isDQ = isHnsPlayerDisqualified(player);
  // Hide and Seek Teams (outLabel): a caught hider is out, and nothing they do counts.
  const isOut = outLabel !== undefined && !!player.capturedAt;
  const canDisqualify = roundIsActive && !isDQ && !isOut;
  // Lifted in play, or after the round ends (the round is scored again).
  const canRevoke = isDQ && (roundIsActive || roundStatus === 'completed');
  const originalRole = player.originalRole || player.role || 'hider';
  const currentRole = player.role || 'hider';
  // Show current role for active/created rounds, original role for completed (scoring context)
  const displayRole = showScore ? originalRole : currentRole;

  return (
    <div className={`round-player ${isDQ ? 'disqualified' : ''}`}>
      <span className={isDQ ? 'player-name-dq' : ''}>
        {getPlayerDisplayName(player.playerId, gamePlayers)}
      </span>
      <span className={roleBadgeClass(displayRole)}>
        {displayRole.toUpperCase()}
      </span>
      {roundIsActive && player.isWearingKrallsAccord && (
        <span className="badge badge--green" title="Wearing Krall's Accord — Marco Polo will auto-respond">
          ⚡ Accord
        </span>
      )}
      {roundIsActive && ruleUseBadges(player, rules).map(({ text, usedUp }) => (
        <span key={text} className={`badge ${usedUp ? 'badge--amber' : 'badge--slate'}`} title="Uses this round, under their role's rules">
          {text}
        </span>
      ))}
      {isDQ && player.disqualification && (
        <span className="badge badge--red" title={player.disqualification.reason}>
          ❌ {getCheatLabel(player.disqualification.cheatType)}
        </span>
      )}
      {showScore && (
        <span className={`ms-auto fw-bold text-blue-light ${isDQ ? 'score-zero' : ''}`}>
          {formatDuration(score)}
          {originalRole === 'hider' && penaltySeconds > 0 && !isDQ && (
            <span className="badge badge--red" title="Marco Polo penalty">
              (−{penaltySeconds}s)
            </span>
          )}
        </span>
      )}
      {player.capturedAt && !isDQ && (outLabel || showScore) && (
        <span className="badge badge--red">{outLabel ?? 'CAUGHT'}</span>
      )}
      {canToggle && (
        <button
          onClick={() => onToggleRole(roundIndex, player.playerId, player.role)}
          className="toggle-role-btn"
        >
          Toggle Role
        </button>
      )}
      {canDisqualify && (
        <button onClick={() => onDisqualify(roundIndex, player.playerId)} className="btn btn--xs btn--red">
          Disqualify
        </button>
      )}
      {canRevoke && (
        <button onClick={() => onRevokeDisqualification(roundIndex, player.playerId)} className="btn btn--xs btn--green">
          Revoke DQ
        </button>
      )}
    </div>
  );
});
